import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { richLinkRemoveLinks, recursiveAddLinks } from '@app/shared/tinymce/plugins/richLink.plugin';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { environment } from '../../../../environments/environment';
import { Globals } from '@app/shared/globals/globals';
import { FRANKLI_TINYMCE_ICONS } from 'assets/tinymce/frankli-icons/icons';
import { RICH_LINK_PLUGIN } from '@app/shared/tinymce/plugins/richLink.plugin';
import { CompanyFeatures } from '@app/models/company-features.model';
import { AI_SHORTCUTS } from '@app/shared/tinymce/ai‑shortcuts.config'; 
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { TranslateService } from '@ngx-translate/core';
import { EditorMessages } from './locale/editor.messages';

declare const tinymce: any;

export const TOOLBAR_DEFAULTS_DESKTOP = 'undo redo | formatting | alignment | bullist numlist checklist | btnFrankliLink | restoredraft';
export const PLUGIN_DEFAULTS_DESKTOP = 'lists, checklist, link, image, table, frankliRichLink, anchor, autolink, codesample, help, tinymcespellchecker, powerpaste, fullscreen, autosave, autoresize';

export const TOOLBAR_DEFAULTS_MOBILE = 'formatselect | formatting | alignment | bullist numlist checklist | btnFrankliLink | restoredraft';
export const PLUGIN_DEFAULTS_MOBILE = 'lists, checklist, link, autolink, frankliRichLink, tinymcespellchecker, powerpaste, autosave, autoresize';
export const MENUBAR_DEFAULTS_MOBILE = false;

@Component({
  selector: 'app-editor-response',
  templateUrl: './editor-response.component.html',
  styleUrls: ['./editor-response.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditorResponseComponent,
      multi: true,
    },
  ],
})
export class EditorResponseComponent implements OnInit, OnDestroy, OnChanges {
  static readonly MAX_DEFAULT = 10000;
  public readonly eEditorMessages = EditorMessages;

  @Input() disabled = false;
  @Input() display = false;
  @Input() resize: boolean;
  @Input() height: number | string;
  @Input() maxCharacters = EditorResponseComponent.MAX_DEFAULT;;   // Maximum / hard max limit (default 10,000).
  @Input() hardMax = EditorResponseComponent.MAX_DEFAULT;
  @Input() inline: boolean;
  @Input() wordcountChanged: EventEmitter<number>;
  @Input() toolbar: string | boolean;
  @Input() toolbarMobile: string | boolean;
  @Input() menubar: string | boolean;
  @Input() menubarMobile: string | boolean;
  @Input() plugins: string;
  @Input() pluginsMobile: string;
  @Input() contextMenu: string;
  @Input() columnIndent: boolean;
  @Input() showErrorBorder: boolean;
  @Input() enforceCharacterLimit: boolean;
  @Input() placeholder: string;
  @Input() autofocus: boolean;
  @Input() id: string;

  @Output() autoEvent: EventEmitter<boolean>;
  @Output() saveRequest = new EventEmitter<string>(); 

  wordcount: number;
  warnWordcount: boolean;
  cachedValue: string;

  editor: any | null;
  _value: string;
  loading: boolean;
  firstInitRun: boolean;
  doRichLinkParsing: boolean;
  dynamicMaxCharacters = 10000;

  onChange = (_: any) => {};
  onTouched = () => {};

  get value(): string {
    return this._value;
  }
  set value(val: string) {
    if (this._value !== val && !this.disabled) {
      this.cachedValue = val;
      this._value = val;
      this.onChange(val);
      this.autoEvent.emit(true);
    }
  }

  public editorConfig!: EditorComponent['init'];
  private localStorageKey = 'editorDynamicMax';
  promptsEnabled   = false;   
  shortcutsEnabled = false; 

  constructor(
    private cdRef: ChangeDetectorRef,
    public globals: Globals,
    private notifyUtils: NotifyUtils,
    private translate: TranslateService

  ) {
    this._value = '';
    this.editor = null;
    this.resize = false;
    this.warnWordcount = false;
    this.inline = false;
    this.columnIndent = true;
    this.showErrorBorder = false;
    this.autofocus = false;
    this.enforceCharacterLimit = true;
    this.doRichLinkParsing = false;

    this.loading = false;
    this.firstInitRun = false;

    this.autoEvent = new EventEmitter<boolean>();
    this.wordcountChanged = new EventEmitter<number>();

    this.wordcount = 0;
    this.height = 300;
    this.cachedValue = '';
    this.menubar = undefined!;
    this.menubarMobile = undefined!;
    this.plugins = undefined!;
    this.pluginsMobile = undefined!;
    this.toolbar = undefined!;
    this.toolbarMobile = undefined!;
    this.placeholder = '';
    this.id = 'rich-editor';
  }

  ngOnInit() {
    // Feature flags & TinyMCE plugin registration
    this.promptsEnabled   = this.globals.hasFeature(CompanyFeatures.AI_PROMPTS);
    this.shortcutsEnabled = this.globals.hasFeature(CompanyFeatures.AI_SHORTCUTS);
    const aiNeeded        = this.promptsEnabled || this.shortcutsEnabled;

    if (tinymce?.PluginManager && tinymce?.IconManager) {
      tinymce.IconManager.add('frankli-icons', { icons: FRANKLI_TINYMCE_ICONS });
      tinymce.PluginManager.add('frankliRichLink', RICH_LINK_PLUGIN);
    }
  
    // Defaults & rich-link parsing flag
    this.setDefaults();
    this.initRichLinkParsing();
  
    // Restore any persisted dynamic max-character setting
    const saved = localStorage.getItem(this.localStorageKey);
    this.dynamicMaxCharacters = saved ? +saved : this.maxCharacters;
  
    // Helper to turn comma-strings into string arrays
    const parseList = (raw?: string): string[] =>
      raw ? raw.split(',').map(s => s.trim()) : [];
  
    //Assemble editorConfig (everything except the setup callback)
    this.editorConfig = {
      // Core
      base_url:             '/assets/tinymce',
      suffix:               '.min',
      license_key:          environment.tinymceLicenseKey,
      selector:             'textarea',
      skin:                 'oxide',
      icons:                'frankli-icons',
  
      // Plugins & toolbar
      plugins:              parseList(this.plugins),
      toolbar:              this.toolbar as string,
      external_plugins:     aiNeeded
        ? { ai: '/assets/tinymce/plugins/ai/plugin.js' }
        : {},
      
      // Mobile configuration
      mobile: {
        menubar: this.menubarMobile,
        toolbar: this.toolbarMobile as string,
        plugins: parseList(this.pluginsMobile)
      },

      // plugins info 
      help_tabs:            ['shortcuts', 'keyboardnav', 'about'],
      spellchecker_rpc_url: `${environment.baseUrl}/ephox-spelling`,
      spellchecker_language:'en_US',
      help_accessibility:   false,
      elementpath:          false,
      branding:             false,
  
      // Layout & paste
      height:               this.height,
      autoresize_min_height:300,
      smart_paste:          false,
      powerpaste_word_import:'clean',
  
      // Autosave & undo
      autosave_interval:        '10s',
      autosave_retention:       '1440m',
      custom_undo_redo_levels:  20,

      setup: (editor) => {
        const comp = this;
        let changeTimer: any;
        this.registerFormattingAndAlignmentButtons(editor);

        editor.on('blur', () => {
          // On blur, convert the current content and update the model.
          const converted = richLinkRemoveLinks(editor.getContent());
          comp.cachedValue = converted;
          comp._value = converted;
          comp.onChange(converted);
          comp.autoEvent.emit(true);
          setTimeout(() => this.saveRequest.emit(editor.getContent()), 0);
        });

        editor.on('init', () => {
          comp.editor = editor;
          comp.loading = false;
          if (comp._value) {
            editor.setContent(comp._value);
            setTimeout(() => {
              if (comp.doRichLinkParsing) {
                recursiveAddLinks(editor.getBody());
              }
            }, 10);
          }
          if (!comp.promptsEnabled && comp.shortcutsEnabled) {
            //Re-register Tiny’s built-in "ai" context toolbar so it shows only the wand (aishortcuts) button. TO DO: change once Tiny supports turning off 'Ask ai' 
            editor.ui.registry.addContextToolbar('ai', {
              predicate: (node) =>
                !editor.selection.isCollapsed() && editor.getBody().contains(node),
              items: 'aishortcuts',
              position: 'selection',
              scope: 'editor'
            });
          }
          if (editor.plugins.wordcount && editor.plugins.wordcount.body) {
            comp.setWordCount(editor.plugins.wordcount.body.getCharacterCount());
          }
          if (comp.disabled) {
            editor.mode.set('readonly');
          }
          comp.firstInitRun = true;
          setTimeout(() => {
            editor.execCommand('mceAutoResize');   // Trigger autoresize after a short delay to ensure the container is properly visible.
          }, 100);
        });    

        // Prevent keystrokes once limit is reached but allow navigation or deletion.
        editor.on('beforeinput', (e: any) => {
          if (!comp.enforceCharacterLimit) return;
          
          if (!e.inputType.startsWith('insert')) return;

          const incoming = typeof e.data === 'string' ? e.data : e.data?.toString() || '';
          const toInsertLen = incoming.length || 1;

          const currentLen = editor.getContent({ format: 'text' }).length;
          const max = comp.dynamicMaxCharacters;

          // If this insertion would exceed the max, block and warn
          if (currentLen + toInsertLen > max) {
            e.preventDefault();
            comp.warnWordcount = true;
            this.notifyUtils.notifyClose();
            comp.notifyUtils.notifyWithParms(
              this.translate.instant(EditorMessages.WARN_CHAR_LIMIT_TYPING, {
                limit: comp.dynamicMaxCharacters
              }),
              { newest_on_top: true, type: 'warning' }
            );            
            comp.cdRef.detectChanges();
          }
        });

        editor.on('PastePreProcess', (e: any) => {
          if (!comp.enforceCharacterLimit) return;
        
          const current = editor.getContent({ format: 'text' }).length;
          const allowed = EditorResponseComponent.MAX_DEFAULT - current;
        
          let plain = e.content.replace(/<[^>]*>/g, '');
          
          if (allowed <= 0) {
            e.content = '';
            this.notifyUtils.notifyClose();
            comp.notifyUtils.notifyWithParms(
              this.translate.instant(EditorMessages.WARN_CHAR_LIMIT_PASTING_AT_LIMIT),
              { newest_on_top: true, type: 'warning' }
            );
          }

          else if (plain.length > allowed) {
            plain = plain.substring(0, allowed);
            e.content = editor.dom.encode(plain);
            this.notifyUtils.notifyClose();
            comp.notifyUtils.notifyWithParms(
              this.translate.instant(EditorMessages.WARN_CHAR_LIMIT_PASTING),
              { newest_on_top: true, type: 'warning' }
            );
          }
        
          comp.warnWordcount = true;
          comp.setWordCount(current + Math.min(plain.length, allowed));
          comp.cdRef.detectChanges();
        });
         

        function onDebouncedChange() {
          clearTimeout(changeTimer);
          changeTimer = setTimeout(() => {
            // Update the model once
            const html = editor.getContent();
            comp.cachedValue = html;
            comp._value = html;
            comp.onChange(html);
            comp.autoEvent.emit(true);
        
            const contentText = editor.getContent({ format: 'text' });
            const newCount = contentText.length;
            comp.setWordCount(newCount);
          }, 300);
        }
      
        // Fire on any user input
        editor.on('input', onDebouncedChange);
      
        // Fire on programmatic inserts 
        editor.on('setcontent', () => {
          onDebouncedChange();
          setTimeout(() => {
            if (comp.doRichLinkParsing) {
              recursiveAddLinks(editor.getBody());
            }
          }, 10);
        });

        editor.on('ResizeEditor', () => {
          comp.updateDynamicMaxCharacters();
        });
        
      },
    };

    /* ---------- AI integration (Prompts & Shortcuts) ---------- */
    if (aiNeeded) {
      if (Array.isArray(this.editorConfig.plugins)) { 
        this.editorConfig.plugins.push('ai');
      } else {
        this.editorConfig.plugins += ' ai';
      }

      if (this.promptsEnabled)   { this.editorConfig.toolbar += ' | aidialog'; }
      if (this.shortcutsEnabled) { this.editorConfig.toolbar += ' | aishortcuts'; }

      this.editorConfig.ai_shortcuts = this.shortcutsEnabled ? AI_SHORTCUTS : false;

      this.editorConfig.ai_request = (tinyReq: any, respondWith: any) => {
        let payload: Record<string, any>;

        if (tinyReq.shortcut) {
          const selection = tinyReq.selection || '';
          const queryText = tinyReq.shortcut.template.replace(/\$\{selection\}/g, selection);

          payload = {
            query:     queryText,
            context:   '',         
            thread:    [],
            system:    [],
            companyId: this.globals.company.id,
          };
        } else {
          payload = {
            ...tinyReq,
            companyId: this.globals.company.id,
          };
        }

        respondWith.string((signal: AbortSignal) =>
          fetch('/api/ai-assistant', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal,
          })
            .then(async (res) => {
              if (!res.ok) throw new Error('Server error ' + res.status);
              const data = await res.json();
              if (data.error) throw new Error(`AI error: ${data.error}`);

              const parsed = JSON.parse(data.answer);
              return parsed.choices[0].message.content;
            })
            .catch((err) => `Network/Server error: ${err.message}`),
        );
      };
    }
  }
/* ---------- end AI integration ---------- */

  ngOnDestroy() {
    this.removeEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {}

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(_e: BeforeUnloadEvent) {
    if (this.editor) {
      const html = this.editor.getContent();
      this.saveRequest.emit(html);           
    }
  }  

  initRichLinkParsing(): void {
    if (this.plugins && this.plugins.includes('frankliRichLink')) {
      this.doRichLinkParsing = true;
    }
  }

  setDefaults() {
    // Desktop defaults
    if (!this.toolbar) {
      this.toolbar = TOOLBAR_DEFAULTS_DESKTOP;
    }
    if (!this.plugins) {
      this.plugins = PLUGIN_DEFAULTS_DESKTOP;
    }

    // Mobile defaults
    if (!this.toolbarMobile) {
      this.toolbarMobile = TOOLBAR_DEFAULTS_MOBILE;
    }
    if (!this.pluginsMobile) {
      this.pluginsMobile = PLUGIN_DEFAULTS_MOBILE;
    }
    if (this.menubarMobile === undefined || this.menubarMobile === null) {
      this.menubarMobile = MENUBAR_DEFAULTS_MOBILE;
    }
  }

  setWordCount(count: number) {
    this.wordcount = count;
    if (count < this.dynamicMaxCharacters) {
      this.warnWordcount = false;
    }
    this.wordcountChanged.emit(count);
    this.cdRef.detectChanges();
  }

  // NG_VALUE_ACCESSOR methods

  writeValue(value: any) {
    if (value === null || value === undefined) {
      value = '';
    }
    this._value = value;
    if (this.editor) {
      this.editor.setContent(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if (this.editor) {
      if (isDisabled) {
        this.editor.mode.set('readonly');
      } else {
        this.editor.mode.set('design');
      }
    }
  }

  removeEditor() {
    if (this.editor) {
      this.editor.remove();
      this.editor = null;
    }
  }

  private updateDynamicMaxCharacters() {
    if (!this.editor) {
      return;
    }
    const containerHeight = this.editor.getContainer().offsetHeight || 300;
    const defaultHeight = 300;
  
    const ratio = containerHeight / defaultHeight;
    const scaled = Math.floor(this.maxCharacters * ratio);
  
    this.dynamicMaxCharacters = Math.min(scaled, 10000);

    localStorage.setItem(this.localStorageKey, this.dynamicMaxCharacters.toString());
  }

  private registerFormattingAndAlignmentButtons(editor: any) {
    console.log('Registering toolbar groups buttons');
    editor.ui.registry.addSplitButton('formatting', {
      icon: 'bold',
      tooltip: 'Formatting',
      onAction: () => editor.execCommand('Bold'),
      fetch: (callback) => callback([
        { type: 'choiceitem', text: 'Bold',      value: 'Bold'      },
        { type: 'choiceitem', text: 'Italic',    value: 'Italic'    },
        { type: 'choiceitem', text: 'Underline', value: 'Underline' }
      ]),
      onItemAction: (api, value) => editor.execCommand(value),
      columns: 1
    });
  
    editor.ui.registry.addSplitButton('alignment', {
      icon: 'align-left',
      tooltip: 'Alignment',
      onAction: () => editor.execCommand('JustifyLeft'),
      fetch: (callback) => callback([
        { type: 'choiceitem', text: 'Left',    value: 'JustifyLeft'    },
        { type: 'choiceitem', text: 'Center',  value: 'JustifyCenter'  },
        { type: 'choiceitem', text: 'Right',   value: 'JustifyRight'   },
        { type: 'choiceitem', text: 'Justify', value: 'JustifyFull'    }
      ]),
      onItemAction: (api, value) => editor.execCommand(value),
      columns: 1
    });
  }

}